import React, { useRef, useState } from 'react';
import { MessageCircle, X } from 'lucide-react';

import AgentChatPanel from './Panel';
import useChatStore from '@/stores/chatStore';

const BUTTON = 56;

const clampLauncher = (x, y) => ({
    x: Math.min(window.innerWidth - BUTTON, Math.max(0, x)),
    y: Math.min(window.innerHeight - BUTTON, Math.max(0, y)),
});

const ChatSplit = ({ children }) => {
    const open = useChatStore((state) => state.open);
    const toggle = useChatStore((state) => state.toggle);
    const launcher = useChatStore((state) => state.launcher);
    const setLauncher = useChatStore((state) => state.setLauncher);
    const drag = useRef(null);
    const [dragging, setDragging] = useState(false);

    const onPointerDown = (event) => {
        const rect = event.currentTarget.getBoundingClientRect();
        drag.current = {
            moved: false,
            originX: event.clientX,
            originY: event.clientY,
            offsetX: event.clientX - rect.left,
            offsetY: event.clientY - rect.top,
        };
        const move = (ev) => {
            if (!drag.current) return;
            const travel = Math.hypot(ev.clientX - drag.current.originX, ev.clientY - drag.current.originY);
            if (travel < 4) return;
            drag.current.moved = true;
            setDragging(true);
            setLauncher(clampLauncher(
                ev.clientX - drag.current.offsetX,
                ev.clientY - drag.current.offsetY,
            ));
        };
        const up = () => {
            window.removeEventListener('pointermove', move);
            window.removeEventListener('pointerup', up);
            const moved = drag.current?.moved;
            drag.current = null;
            setDragging(false);
            if (!moved) toggle();
        };
        window.addEventListener('pointermove', move);
        window.addEventListener('pointerup', up);
    };

    return (
        <div className="relative flex h-screen w-full min-h-0 overflow-hidden">
            {open && (
                <div
                    data-agent-chat
                    className="relative z-20 flex h-full w-[30%] min-w-0 shrink-0 overflow-hidden border-r border-border bg-card"
                >
                    <AgentChatPanel />
                </div>
            )}
            <div className="relative z-0 flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
                {children}
            </div>
            <button
                type="button"
                onPointerDown={onPointerDown}
                aria-label="Chat with AI"
                title="Chat with AI"
                className={`fixed z-[60] flex h-14 w-14 touch-none items-center justify-center rounded-full bg-primary text-primary-foreground cursor-pointer ${
                    launcher ? '' : 'bottom-5 right-5'
                } ${open || dragging ? '' : 'chat-launcher-pop'}`}
                style={{
                    cursor: dragging ? 'grabbing' : 'pointer',
                    ...(launcher ? { left: launcher.x, top: launcher.y } : {}),
                }}
            >
                {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
                <span className="sr-only">Chat with AI</span>
            </button>
        </div>
    );
};

export default ChatSplit;
